import React from "react";
import { SLAPolicy } from "../types";

/**
 * Reusable SLA policy editor — 4 priority rows × 2 columns (response,
 * resolution), values in minutes. Same shape used by the global Settings
 * page and the per-customer override on CustomerDetail.
 *
 * Values are minutes. The presenter shows an auxiliary "hours" hint so
 * MSPs entering values like 240 can sanity-check it's 4 hours.
 */

type Priority = "urgent" | "high" | "medium" | "low";

const PRIORITY_ROWS: { key: Priority; label: string; color: string }[] = [
  { key: "urgent", label: "Urgent", color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  { key: "high", label: "High", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" },
  { key: "medium", label: "Medium", color: "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  { key: "low", label: "Low", color: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300" },
];

function fmtHours(mins: number): string {
  if (!Number.isFinite(mins) || mins <= 0) return "—";
  if (mins < 60) return `${mins}m`;
  const h = mins / 60;
  return h % 1 === 0 ? `${h}h` : `${h.toFixed(1)}h`;
}

interface Props {
  value: SLAPolicy;
  onChange: (next: SLAPolicy) => void;
  disabled?: boolean;
}

export default function SLAPolicyEditor({ value, onChange, disabled }: Props) {
  const setMins = (key: keyof SLAPolicy, n: number) => onChange({ ...value, [key]: n });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <th className="py-2 pr-3 font-medium">Priority</th>
            <th className="py-2 pr-3 font-medium">Response (min)</th>
            <th className="py-2 pr-3 font-medium w-16 text-gray-400 dark:text-gray-500">≈</th>
            <th className="py-2 pr-3 font-medium">Resolution (min)</th>
            <th className="py-2 pr-3 font-medium w-16 text-gray-400 dark:text-gray-500">≈</th>
          </tr>
        </thead>
        <tbody>
          {PRIORITY_ROWS.map((row) => {
            const respKey = `response_${row.key}_minutes` as keyof SLAPolicy;
            const resKey = `resolution_${row.key}_minutes` as keyof SLAPolicy;
            const respMins = Number(value[respKey] ?? 0);
            const resMins = Number(value[resKey] ?? 0);
            return (
              <tr key={row.key} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                <td className="py-2 pr-3">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${row.color}`}
                  >
                    {row.label}
                  </span>
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="number"
                    min={1}
                    disabled={disabled}
                    value={respMins}
                    onChange={(e) => setMins(respKey, Number(e.target.value))}
                    className="w-28 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500"
                  />
                </td>
                <td className="py-2 pr-3 text-xs text-gray-400 dark:text-gray-500">{fmtHours(respMins)}</td>
                <td className="py-2 pr-3">
                  <input
                    type="number"
                    min={1}
                    disabled={disabled}
                    value={resMins}
                    onChange={(e) => setMins(resKey, Number(e.target.value))}
                    className="w-28 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500"
                  />
                </td>
                <td className="py-2 pr-3 text-xs text-gray-400 dark:text-gray-500">{fmtHours(resMins)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
